import { Fragment, useContext, useState, type FormEvent } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { LogoLoader } from '@/components/ui/logo-loader';
import { cn } from '@/lib/utils';

import { BrowserRequestPresentationContext } from './browser-request-presentation';
import { CredentialNotificationRetry } from './credential-notification-retry';
import { CredentialOtpInput } from './credential-otp-input';
import { CredentialQrImage } from './credential-qr-image';
import { CredentialRequestOverlay } from './credential-request-overlay';
import {
  type SignInSession,
  useSignInSessionActions,
} from './sign-in-session.query';

export const CredentialRequestForm = ({
  request,
  onBeforeSubmit,
  overlay = false,
}: {
  request: SignInSession;
  onBeforeSubmit?: () => void | Promise<void>;
  overlay?: boolean;
}) => {
  const presentation = useContext(BrowserRequestPresentationContext);
  const cardLayout = !overlay || Boolean(presentation?.inlineTarget);
  const [values, setValues] = useState<Record<string, string | string[]>>({});
  const [visiblePasswords, setVisiblePasswords] = useState<
    Record<string, boolean>
  >({});
  const [submitted, setSubmitted] = useState(false);
  const { respond, resume } = useSignInSessionActions(
    request.sign_in_session_id,
    request.chat_id,
    request.request?.input_request_id,
    request.expires_at
  );
  const busy = respond.isPending || resume.isPending;
  const step = request.request;
  const pending = request.status === 'active' && step?.status === 'pending';
  const showInput = pending && !busy && (!submitted || respond.isError);
  const cancelled = step?.result?.action === 'close';
  const needsNotification =
    !busy && step?.status === 'resolved' && !step.notified;
  const inProgress =
    busy || (request.status === 'active' && !needsNotification);

  const submit = async (
    event: FormEvent<HTMLFormElement>,
    sectionId: string,
    fields: NonNullable<SignInSession['request']>['sections'][number]['fields']
  ) => {
    event.preventDefault();
    if (!showInput || busy) return;
    if (
      fields.some(
        (field) =>
          field.type === 'otp' &&
          !new RegExp(`^[A-Za-z0-9]{${field.max_length}}$`).test(
            (values[field.id] as string[] | undefined)?.join('') ?? ''
          )
      )
    )
      return;
    const submittedValues = Object.fromEntries(
      fields.map((field) => [
        field.id,
        field.type === 'otp'
          ? (values[field.id] as string[]).join('')
          : ((values[field.id] as string | undefined) ??
            field.initial_value ??
            ''),
      ])
    );
    setValues({});
    setVisiblePasswords({});
    setSubmitted(true);
    await respond.submit(
      { action: 'fill', section_id: sectionId, values: submittedValues },
      onBeforeSubmit
    );
  };

  const sendAction = async (
    action:
      | { action: 'choose'; section_id: string; choice_id: string }
      | { action: 'back' | 'close' }
  ) => {
    if (!showInput || busy) return;
    setValues({});
    setVisiblePasswords({});
    setSubmitted(true);
    await respond.submit(action, onBeforeSubmit);
  };

  if (cancelled || (overlay && respond.dismissed)) return null;
  if (overlay && needsNotification) {
    return (
      <CredentialNotificationRetry
        pending={busy}
        isError={resume.isError}
        onRetry={() => void resume.mutate()}
      />
    );
  }
  if (overlay && !showInput) return null;

  const content = (
    <YStack
      className={cn(
        'min-w-0 rounded-xl border bg-surface text-foreground',
        cardLayout
          ? 'border-border-strong overflow-hidden'
          : 'gap-5 border-border p-4 sm:p-6'
      )}
      data-testid="credential-request-form"
    >
      <XStack
        className={cn(
          'justify-between',
          cardLayout
            ? 'items-start gap-4 border-b border-border px-4 py-4'
            : 'items-center gap-2'
        )}
      >
        <YStack className="min-w-0 gap-1">
          <h2 className="break-words text-base leading-snug font-medium">
            {cardLayout && showInput && step.sections.length === 1
              ? step.sections[0].label
              : 'Browser request'}
          </h2>
          {showInput ? (
            <p className="break-all text-sm leading-relaxed text-foreground-muted">
              {new URL(step.origin).origin}
            </p>
          ) : null}
        </YStack>
        <XStack className="shrink-0 items-center gap-1">
          {showInput && step.allow_back ? (
            <IconButton
              icon={Icons.arrowLeft}
              strokeWidth={1.8}
              aria-label="Back"
              title="Back"
              size={cardLayout ? 'lg' : 'xl'}
              disabled={busy}
              onClick={() => void sendAction({ action: 'back' })}
            />
          ) : null}
          {overlay && showInput && !presentation?.inlineTarget ? (
            <IconButton
              icon={Icons.cancel}
              strokeWidth={1.8}
              aria-label="Close sign-in"
              title="Close sign-in"
              size="xl"
              disabled={busy}
              onClick={() => void sendAction({ action: 'close' })}
            />
          ) : null}
        </XStack>
      </XStack>
      {!showInput ? (
        <YStack center className="min-h-40 gap-3 px-4 py-8 text-center">
          {inProgress ? (
            <YStack aria-hidden="true" className="size-8 text-foreground">
              <LogoLoader activity="think" flightOnly />
            </YStack>
          ) : null}
          <p role="status" className="text-base font-medium">
            {busy
              ? 'Sending your response…'
              : needsNotification
                ? 'Your response hasn’t reached Littlebird'
                : request.status === 'success'
                  ? 'Signed in'
                  : request.status === 'timedout'
                    ? 'Sign-in timed out'
                    : request.status === 'failure'
                      ? 'Request couldn’t be completed'
                      : 'Working on the next step…'}
          </p>
          {!needsNotification && !busy && request.status !== 'active' ? (
            <p className="text-sm leading-relaxed text-foreground-muted">
              {request.status === 'success'
                ? 'You can return to your conversation.'
                : request.status === 'timedout'
                  ? 'Sign-ins time out after 30 minutes. Ask Littlebird to start a fresh sign-in in your conversation.'
                  : 'Return to your conversation to try again.'}
            </p>
          ) : null}
        </YStack>
      ) : null}
      {showInput && respond.isError ? (
        <p
          role="alert"
          className={cn(
            'text-sm leading-relaxed text-danger-11',
            cardLayout && 'px-4 pt-4'
          )}
        >
          Couldn’t send. Please try again.
        </p>
      ) : null}
      {showInput
        ? step.sections.map((section, sectionIndex) => (
            <YStack
              key={section.id}
              role="group"
              aria-label={section.label}
              className={cn(
                cardLayout ? 'gap-0' : 'gap-3',
                cardLayout && sectionIndex > 0 && 'border-t border-border'
              )}
            >
              {!cardLayout || step.sections.length > 1 ? (
                <p
                  className={cn(
                    'break-words text-sm leading-relaxed font-medium',
                    cardLayout &&
                      'border-b border-border bg-surface-hover px-4 py-3'
                  )}
                >
                  {section.label}
                </p>
              ) : null}
              {section.fields.length > 0 ? (
                <form
                  onSubmit={(event) =>
                    void submit(event, section.id, section.fields)
                  }
                  autoComplete="off"
                >
                  <YStack className={cardLayout ? 'gap-0' : 'gap-4'}>
                    {section.fields.map((field) => (
                      <YStack
                        key={field.id}
                        className={cn(
                          'min-w-0',
                          cardLayout ? 'gap-2 px-4 py-4' : 'gap-2'
                        )}
                      >
                        <label
                          className="text-sm font-medium"
                          htmlFor={`credential-${request.sign_in_session_id}-${field.id}`}
                        >
                          {field.label}
                        </label>
                        {field.type === 'otp' ? (
                          <CredentialOtpInput
                            id={`credential-${request.sign_in_session_id}-${field.id}`}
                            label={field.label}
                            length={field.max_length}
                            slots={
                              (values[field.id] as string[] | undefined) ?? []
                            }
                            onChange={(slots) =>
                              setValues((current) => ({
                                ...current,
                                [field.id]: slots,
                              }))
                            }
                          />
                        ) : (
                          <XStack className="relative items-center">
                            <input
                              id={`credential-${request.sign_in_session_id}-${field.id}`}
                              name={field.id}
                              type={
                                field.type === 'password' &&
                                visiblePasswords[field.id]
                                  ? 'text'
                                  : field.type === 'redirect_url'
                                    ? 'url'
                                    : field.type
                              }
                              required
                              autoComplete="off"
                              value={
                                (values[field.id] as string | undefined) ??
                                field.initial_value ??
                                ''
                              }
                              onChange={(event) =>
                                setValues((current) => ({
                                  ...current,
                                  [field.id]: event.target.value,
                                }))
                              }
                              placeholder={
                                cardLayout ? 'Type here…' : undefined
                              }
                              className={cn(
                                'min-h-11 min-w-0 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground placeholder:text-foreground-subtle outline-none focus-visible:ring-2 focus-visible:ring-accent',
                                field.type === 'password' && 'pr-11'
                              )}
                            />
                            {field.type === 'password' ? (
                              <IconButton
                                type="button"
                                icon={
                                  visiblePasswords[field.id]
                                    ? Icons.eyeOff
                                    : Icons.eye
                                }
                                aria-label={
                                  visiblePasswords[field.id]
                                    ? `Hide ${field.label}`
                                    : `Show ${field.label}`
                                }
                                title={
                                  visiblePasswords[field.id]
                                    ? 'Hide password'
                                    : 'Show password'
                                }
                                size="lg"
                                className="absolute right-0.5"
                                onClick={() =>
                                  setVisiblePasswords((current) => ({
                                    ...current,
                                    [field.id]: !current[field.id],
                                  }))
                                }
                              />
                            ) : null}
                          </XStack>
                        )}
                      </YStack>
                    ))}
                    <XStack
                      className={
                        cardLayout
                          ? 'justify-end border-t border-border px-4 py-3'
                          : ''
                      }
                    >
                      <Button
                        type="submit"
                        variant={cardLayout ? 'ghost' : 'default'}
                        className={
                          cardLayout
                            ? 'min-h-11 w-full bg-gray-12 px-4 text-gray-1 hover:bg-gray-11 hover:enabled:text-gray-1 sm:w-auto'
                            : 'min-h-11 w-full'
                        }
                        disabled={busy}
                      >
                        {section.fields[0]?.type === 'redirect_url'
                          ? 'Open link'
                          : 'Continue'}
                        {cardLayout ? (
                          <Icons.navigateRight className="ml-1 size-4" />
                        ) : null}
                      </Button>
                    </XStack>
                  </YStack>
                </form>
              ) : null}
              {section.qr_image_file_id ? (
                <YStack
                  className={
                    cardLayout ? 'items-center px-4 py-6' : 'items-center'
                  }
                >
                  <CredentialQrImage
                    key={section.qr_image_file_id}
                    fileId={section.qr_image_file_id}
                  />
                </YStack>
              ) : null}
              {section.choices.map((choice, index) => (
                <Fragment key={choice.id}>
                  {cardLayout && index > 0 ? (
                    <XStack
                      aria-hidden="true"
                      className="mx-4 h-px bg-border"
                    />
                  ) : null}
                  <Button
                    variant={cardLayout ? 'ghost' : 'secondary'}
                    className={cn(
                      'h-auto min-h-11 [&>span]:w-full [&>span]:justify-start [&>span]:break-words [&>span]:text-left [&>span]:whitespace-normal',
                      cardLayout
                        ? 'mx-2 rounded-lg px-2 py-3 hover:bg-surface-hover last:mb-2'
                        : 'w-full'
                    )}
                    disabled={busy}
                    onClick={() =>
                      void sendAction({
                        action: 'choose',
                        section_id: section.id,
                        choice_id: choice.id,
                      })
                    }
                  >
                    {cardLayout ? (
                      <span
                        aria-hidden="true"
                        className="mr-1 flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-surface-hover text-sm text-foreground-muted"
                      >
                        {index + 1}
                      </span>
                    ) : null}
                    {choice.label}
                  </Button>
                </Fragment>
              ))}
            </YStack>
          ))
        : null}
      {needsNotification && resume.isError ? (
        <p
          role="alert"
          className="px-4 pb-2 text-sm leading-relaxed text-danger-11"
        >
          Couldn’t send. Please try again.
        </p>
      ) : null}
      {needsNotification ? (
        <Button
          className="m-4 min-h-11"
          disabled={busy}
          onClick={() => void resume.mutate()}
        >
          Send again
        </Button>
      ) : null}
    </YStack>
  );

  return overlay ? (
    <CredentialRequestOverlay label="Browser request">
      {content}
    </CredentialRequestOverlay>
  ) : (
    content
  );
};
